import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export default function Onboarding() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Form data
  const [ptaName, setPtaName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [charityNumber, setCharityNumber] = useState("");
  const [ptaEmail, setPtaEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 2) {
      setStep(step + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      // Create the PTA
      const { data: ptaData, error: ptaError } = await supabase
        .from('ptas')
        .insert({
          name: ptaName,
          school_name: schoolName,
          charity_number: charityNumber || null,
          shared_email: ptaEmail || null,
        })
        .select()
        .single();

      if (ptaError) throw ptaError;

      // Create member record with PTA admin role
      const { error: memberError } = await supabase
        .from('members')
        .insert({
          user_id: user.id,
          pta_id: ptaData.id,
          full_name: user.user_metadata?.full_name || user.email || '',
          email: user.email || '',
          is_pta_admin: true,
        });

      if (memberError) throw memberError;

      // Update subscription to link to PTA
      const { error: subError } = await supabase
        .from('subscriptions')
        .update({ pta_id: ptaData.id })
        .eq('user_id', user.id);

      if (subError) throw subError;

      toast({
        title: "Welcome to PTA Hub!",
        description: "Your PTA has been created successfully.",
      });
      navigate("/pta-admin");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create PTA. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <Card className="w-full max-w-2xl shadow-elevated">
        <CardHeader>
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              {[1, 2].map((s) => (
                <div
                  key={s}
                  className={`h-2 w-24 rounded-full transition-colors ${
                    s <= step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">Step {step} of 2</span>
          </div>
          <CardTitle className="text-2xl">
            {step === 1 && "PTA Information"}
            {step === 2 && "Contact Details"}
          </CardTitle>
          <CardDescription>
            {step === 1 && "Tell us about your PTA"}
            {step === 2 && "How can people reach your PTA? (Optional)"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleNext} className="space-y-4">
            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="pta-name">PTA Name *</Label>
                  <Input 
                    id="pta-name" 
                    placeholder="Hillside Primary PTA" 
                    value={ptaName}
                    onChange={(e) => setPtaName(e.target.value)}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school-name">School Name *</Label>
                  <Input 
                    id="school-name" 
                    placeholder="Hillside Primary School" 
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="charity-number">Charity Number (Optional)</Label>
                  <Input 
                    id="charity-number" 
                    placeholder="123456" 
                    value={charityNumber}
                    onChange={(e) => setCharityNumber(e.target.value)}
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">PTA Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="pta@school.com" 
                    value={ptaEmail}
                    onChange={(e) => setPtaEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input 
                    id="phone" 
                    type="tel" 
                    placeholder="+44 123 456 7890" 
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                  />
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">You're all set!</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        You'll be set as the PTA Admin and can invite members from your dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-4">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Back
                </Button>
              )}
              <Button type="submit" className="flex-1" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating PTA...
                  </>
                ) : (
                  step === 2 ? "Create PTA" : "Continue"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
